import React from 'react';
import cn from 'classnames';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import ProfileForm from './ProfileForm';
import Layout from '../../components/layout/Layout';
import {
  useGetCustomerQuery,
  useUpdateCustomerMutation,
} from '../../redux/customersApi';
import {
  invalidPhoneMsg,
  isValidPhone,
  requiredValidationHelper,
} from '../../utils/validations';
import { removeSession } from '../../utils/session';
import { clearGladlyUser } from '../../utils/gladly';
import { LOGIN } from '../../routes';
import { LOGOUT } from '../../redux/types';
import Loader from '../../components/common/ui/Loader';
import {
  parseRechargeSdkErrorMessage,
  setFormikError,
} from '../../utils/errors';
import useNoty from '../../hooks/useNoty';
import { removeSpecialsFromPhone } from './utils';

import styles from './styles.module.scss';

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const noty = useNoty();

  const { data: customer, isLoading } = useGetCustomerQuery();
  const [updateCustomer, { isLoading: isUpdating }] =
    useUpdateCustomerMutation();

  const handleUpdateProfile = async (values) => {
    formik.setStatus('');

    const params = { ...values };
    if (params.phone) {
      params.phone = removeSpecialsFromPhone(params.phone);
    }

    try {
      const resp = await updateCustomer(params);
      if (resp.error) {
        const rechargeError = parseRechargeSdkErrorMessage(resp.error);
        if (rechargeError && JSON.stringify(rechargeError) !== '{}') {
          setFormikError(formik, rechargeError);
        } else {
          noty.error('Failed to update personal info');
        }
      } else {
        noty.success('Your personal info updated');
      }
    } catch (error) {
      noty.error('Failed to update personal info');
    } finally {
      formik.setSubmitting(false);
    }
  };

  const handleLogout = () => {
    removeSession();
    clearGladlyUser();
    dispatch({ type: LOGOUT });
    navigate(LOGIN);
  };

  const formik = useFormik({
    initialValues: {
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      phone: customer?.phone || '',
    },
    enableReinitialize: true,
    onSubmit: handleUpdateProfile,
    validate,
  });

  return (
    <Layout className={styles.page}>
      <h4 className={cn('h4', styles.subtitle)}>Profile</h4>
      <h2 className={cn('h2', styles.title)}>
        {customer?.first_name} {customer?.last_name}
      </h2>
      <ProfileForm
        customer={customer}
        formik={formik}
        handleLogout={handleLogout}
      />
      <Loader show={isLoading || isUpdating} />
    </Layout>
  );
};

const validate = (values) => {
  let errors = {};
  const requiredFields = ['first_name', 'last_name'];

  requiredValidationHelper(values, errors, requiredFields);

  if (values.phone && !isValidPhone(removeSpecialsFromPhone(values.phone))) {
    errors.phone = invalidPhoneMsg;
  }

  return errors;
};

export default Profile;
